import { useRef } from 'react';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';

function toCss(width: unknown): string | undefined {
  if (width == null || width === '') return undefined;
  return typeof width === 'number' ? `${width}px` : String(width);
}

/** Image with a corner drag-handle to resize (shrink/grow) its width; aspect ratio is preserved. */
export default function ImageNodeView({ node, updateAttributes, editor, selected }: NodeViewProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const { src, alt, title, width } = node.attrs as { src: string; alt?: string; title?: string; width?: unknown };

  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const img = imgRef.current;
    if (!img) return;
    const startX = e.clientX;
    const startW = img.offsetWidth;
    // Force the resize cursor for the whole drag and suppress text selection,
    // then restore both on release so the cursor returns to normal when you click away.
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'nwse-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev: PointerEvent) => {
      const next = Math.max(40, Math.round(startW + (ev.clientX - startX)));
      updateAttributes({ width: next });
    };
    const onUp = () => {
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  return (
    <NodeViewWrapper className={`rte-img-wrap${selected ? ' selected' : ''}`}>
      <img ref={imgRef} src={src} alt={alt || ''} title={title || undefined} style={{ width: toCss(width) }} />
      {editor.isEditable && <span className="rte-img-handle" title="Drag to resize" onPointerDown={startResize} />}
    </NodeViewWrapper>
  );
}
